/**
 * The doors (spec 02 §9.3): the red door to the secret room and the white door that warps to
 * level 70. Bigger than the other power-ups, about 2x2 tiles, standing on the base at y = 0 and
 * facing the camera (+z): an arched plank door in its colour, in a gold frame on a stone step.
 */

import { Vector3 } from 'three';
import { place } from '../../kit/MeshBuilder';
import type { Builder } from './charms';
import { arc, rect, rotX, slab, Smooth, tube } from './shapes';

/** Half the door's width, where the arch springs from, and the frame's thickness. */
const W = 0.62;
const SPRING = 1.28;
const T = 0.13;

/** The door leaf's outline: straight sides, a round arch, counter-clockwise. */
function leafShape(bottom: number): Array<[number, number]> {
  const pts: Array<[number, number]> = [[-W, bottom], [W, bottom]];
  for (let i = 0; i <= 14; i++) {
    const a = arc(0, SPRING, W, (i / 14) * Math.PI);
    pts.push([a.x, a.y]);
  }
  return pts;
}

/** An arched door in `ramp` (light for the bevel, dark for the grooves) with a gold frame. */
export const door = (ramp: string): Builder => (mb, p) => {
  const s = new Smooth();
  const step = 0.1;
  mb.box(1.72, step, 0.56, { all: p.color('puWhite.0'), top: p.color('puWhite.1') }, place(0, 0, 0.04));
  slab(s, leafShape(step), 0.14, p.color(`${ramp}.1`), {
    bevel: 0.05, bevelColor: p.color(`${ramp}.2`), sideColor: p.color(`${ramp}.0`),
  });
  s.flush(mb);
  // The planks' grooves, stopping under the arch.
  for (const x of [-0.31, 0, 0.31]) {
    const top = SPRING + Math.sqrt(W * W - x * x) - 0.12;
    mb.box(0.035, top - step - 0.08, 0.02, p.color(`${ramp}.0`), place(x, step + 0.05, 0.072));
  }
  // The frame: two posts and the arch over them, a keystone at the top.
  const gold = p.color('puGold.1');
  const frame: Vector3[] = [new Vector3(W + T / 2, step, 0)];
  for (let i = 0; i <= 18; i++) frame.push(arc(0, SPRING, W + T / 2, (i / 18) * Math.PI));
  frame.push(new Vector3(-W - T / 2, step, 0));
  tube(s, frame, T * 0.75, 10, gold);
  s.flush(mb, place(0, 0, 0.02));
  slab(s, [[-0.13, 0], [0.13, 0], [0.09, 0.26], [-0.09, 0.26]], 0.2, p.color('puGold.2'), {
    bevel: 0.03, bevelColor: p.color('puGold.2'), sideColor: p.color('puGold.0'),
  });
  s.flush(mb, place(0, SPRING + W - 0.06, 0.04));
  // Iron hinges on the left, a gold ring knob on the right, a barred window up top.
  for (const y of [0.42, 1.2]) {
    slab(s, rect(-0.36, y, 0.42, 0.08), 0.03, p.color('puSteel.1'), { sideColor: p.color('puSteel.0') });
    mb.blob(0.04, 0.04, 0.03, p.color('puSteel.2'), place(-0.2, y, 0.1), { detail: 1 });
  }
  s.flush(mb, place(0, 0, 0.08));
  mb.blob(0.08, 0.08, 0.06, p.color('puGold.1'), place(0.4, 0.86, 0.1), { detail: 2 });
  tube(s, [0, 1, 2, 3, 4, 5, 6, 7].map((i) => arc(0.4, 0.78, 0.08, (i / 8) * Math.PI * 2)), 0.022, 6, gold, {
    closed: true,
  });
  s.flush(mb, place(0, 0, 0.1));
  mb.cylinder(0.17, 0.17, 0.03, 16, p.color('ink.1'), rotX(0, 1.6, 0.06, Math.PI / 2));
  tube(s, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => arc(0, 1.6, 0.17, (i / 12) * Math.PI * 2)), 0.03, 6, gold, {
    closed: true,
  });
  s.flush(mb, place(0, 0, 0.09));
  mb.box(0.03, 0.34, 0.03, p.color('puSteel.2'), place(0, 1.6, 0.1), true);
  mb.box(0.34, 0.03, 0.03, p.color('puSteel.2'), place(0, 1.6, 0.1), true);
};
